import React, { useState, useEffect } from "react";
import { formatEnglishDate } from "../../utils/dateFormatting";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { adminApi } from "../../services/api";

const AdminDashboardStats = ({ adminKey, walletAddress }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [period, setPeriod] = useState("all");

  useEffect(() => {
    fetchStats();
  }, [period, adminKey]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await adminApi.getEsgDashboardStats(adminKey, walletAddress);
      setStats(response);
    } catch (err) {
      console.error("Error fetching stats:", err);
      setError(err.message || "Errore nel caricamento delle statistiche");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: "center" }}>
        <div style={{ fontSize: 16, color: "#94a3b8" }}>
          Caricamento statistiche...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          padding: 24,
          textAlign: "center",
          background: "rgba(248,113,113,0.1)",
          borderRadius: 12,
          border: "1px solid rgba(248,113,113,0.3)",
        }}
      >
        <div style={{ fontSize: 16, color: "#f97373" }}>{error}</div>
        <button
          onClick={fetchStats}
          style={{
            marginTop: 12,
            padding: "8px 16px",
            background: "#3b82f6",
            color: "#fff",
            border: "none",
            borderRadius: 6,
            cursor: "pointer",
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          Riprova
        </button>
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  // Prepara dati per chart distribuzione livelli
  const levelDistributionData = Object.entries(stats.levelDistribution).map(
    ([level, count]) => ({
      name: getLevelLabel(level),
      value: count,
      fill: getLevelColor(level),
    })
  );

  // Prepara dati per chart badge più comuni
  const topBadgesData = stats.topBadges.slice(0, 5).map((badge) => ({
    name: badge.name,
    value: badge.count,
    percentage: badge.percentage,
    category: badge.category,
  }));

  // Calcola metriche IPFS
  const ipfsMetrics = stats.ipfsStats || {};
  const ipfsPublished = ipfsMetrics.totalPublished || 0;
  const ipfsTotal = ipfsMetrics.totalCertifications || 0;
  const ipfsRate = ipfsMetrics.publishRate || 0;

  // Calcola metriche evidenze
  const evidenceMetrics = stats.evidenceStats || {};
  const avgEvidence = parseFloat(evidenceMetrics.avgPerCertification) || 0;

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 32,
        }}
      >
        <h1 style={{ fontSize: 28, fontWeight: 700, margin: 0, color: "#f8fafc" }}>
          Dashboard Statistiche ESG
        </h1>

        {/* Period selector */}
        <div style={{ display: "flex", gap: 8 }}>
          {["7d", "30d", "90d", "all"].map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              style={{
                padding: "8px 16px",
                borderRadius: 6,
                border: "1px solid " + (period === p ? "#3b82f6" : "rgba(148,163,184,0.3)"),
                background: period === p ? "rgba(59,130,246,0.2)" : "transparent",
                color: period === p ? "#3b82f6" : "#94a3b8",
                cursor: "pointer",
                fontSize: 13,
                fontWeight: 600,
                transition: "all 0.2s ease",
              }}
            >
              {p === "all" ? "Tutto" : "Ultimi " + p.replace("d", "g")}
            </button>
          ))}
        </div>
      </div>

      {/* KPI Cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: 16,
          marginBottom: 32,
        }}
      >
        {/* Total Certifications */}
        <div
          style={{
            padding: 20,
            borderRadius: 12,
            background: "linear-gradient(135deg, rgba(59,130,246,0.1), rgba(37,99,235,0.05))",
            border: "1px solid rgba(59,130,246,0.3)",
          }}
        >
          <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.5px" }}>
            Certificazioni
          </div>
          <div style={{ fontSize: 32, fontWeight: 700, color: "#3b82f6" }}>
            {ipfsTotal}
          </div>
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 8 }}>
            Totale nel sistema
          </div>
        </div>

        {/* IPFS Published */}
        <div
          style={{
            padding: 20,
            borderRadius: 12,
            background: "linear-gradient(135deg, rgba(16,185,129,0.1), rgba(5,150,105,0.05))",
            border: "1px solid rgba(16,185,129,0.3)",
          }}
        >
          <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.5px" }}>
            IPFS Pubblicato
          </div>
          <div style={{ fontSize: 32, fontWeight: 700, color: "#10b981" }}>
            {ipfsPublished}
          </div>
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 8 }}>
            {ipfsRate}% tasso di pubblicazione
          </div>
        </div>

        {/* Avg Evidence */}
        <div
          style={{
            padding: 20,
            borderRadius: 12,
            background: "linear-gradient(135deg, rgba(139,92,246,0.1), rgba(109,40,217,0.05))",
            border: "1px solid rgba(139,92,246,0.3)",
          }}
        >
          <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.5px" }}>
            Evidenze Medie
          </div>
          <div style={{ fontSize: 32, fontWeight: 700, color: "#8b5cf6" }}>
            {avgEvidence.toFixed(1)}
          </div>
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 8 }}>
            Per certificazione
          </div>
        </div>

        {/* Top Level */}
        <div
          style={{
            padding: 20,
            borderRadius: 12,
            background: "linear-gradient(135deg, rgba(245,158,11,0.1), rgba(217,119,6,0.05))",
            border: "1px solid rgba(245,158,11,0.3)",
          }}
        >
          <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.5px" }}>
            Livello Più Comune
          </div>
          <div style={{ fontSize: 32, fontWeight: 700, color: "#f59e0b" }}>
            {getTopLevel(stats.levelDistribution)}
          </div>
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 8 }}>
            {stats.levelDistribution[getTopLevel(stats.levelDistribution)]} aziende
          </div>
        </div>
      </div>

      {/* Charts Row 1 */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))",
          gap: 24,
          marginBottom: 32,
        }}
      >
        {/* Level Distribution */}
        <div
          style={{
            padding: 20,
            borderRadius: 12,
            background: "rgba(15,23,42,0.8)",
            border: "1px solid rgba(124,255,178,0.2)",
            backdropFilter: "blur(10px)",
          }}
        >
          <h3 style={{ fontSize: 16, fontWeight: 700, marginTop: 0, marginBottom: 16, color: "#f8fafc" }}>
            Distribuzione Livelli
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={levelDistributionData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.1)" />
              <XAxis dataKey="name" stroke="#94a3b8" />
              <YAxis stroke="#94a3b8" />
              <Tooltip
                contentStyle={{
                  background: "rgba(15,23,42,0.95)",
                  border: "1px solid rgba(124,255,178,0.3)",
                  borderRadius: 8,
                  color: "#f8fafc",
                }}
              />
              <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                {levelDistributionData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Top Badges */}
        <div
          style={{
            padding: 20,
            borderRadius: 12,
            background: "rgba(15,23,42,0.8)",
            border: "1px solid rgba(124,255,178,0.2)",
            backdropFilter: "blur(10px)",
          }}
        >
          <h3 style={{ fontSize: 16, fontWeight: 700, marginTop: 0, marginBottom: 16, color: "#f8fafc" }}>
            Top 5 Badge Ottenuti
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={topBadgesData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.1)" />
              <XAxis type="number" stroke="#94a3b8" />
              <YAxis dataKey="name" type="category" stroke="#94a3b8" width={120} fontSize={12} />
              <Tooltip
                contentStyle={{
                  background: "rgba(15,23,42,0.95)",
                  border: "1px solid rgba(124,255,178,0.3)",
                  borderRadius: 8,
                  color: "#f8fafc",
                }}
                formatter={(value) => `${value} aziende (${topBadgesData.find(d => d.value === value)?.percentage || 0}%)`}
              />
              <Bar dataKey="value" fill="#3b82f6" radius={[0, 8, 8, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))",
          gap: 24,
        }}
      >
        {/* Evidence by Type */}
        {evidenceMetrics.byType && (
          <div
            style={{
              padding: 20,
              borderRadius: 12,
              background: "rgba(15,23,42,0.8)",
              border: "1px solid rgba(124,255,178,0.2)",
              backdropFilter: "blur(10px)",
            }}
          >
            <h3 style={{ fontSize: 16, fontWeight: 700, marginTop: 0, marginBottom: 16, color: "#f8fafc" }}>
              Evidenze per Tipo
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={Object.entries(evidenceMetrics.byType).map(([type, count]) => ({
                    name: type,
                    value: count,
                    fill: getEvidenceTypeColor(type),
                  }))}
                  cx="50%"
                  cy="50%"
                  labelLine={true}
                  label={({ name, value }) => `${name}: ${value}`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {Object.entries(evidenceMetrics.byType).map((_, index) => (
                    <Cell key={`cell-${index}`} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    background: "rgba(15,23,42,0.95)",
                    border: "1px solid rgba(124,255,178,0.3)",
                    borderRadius: 8,
                    color: "#f8fafc",
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Last Published Info */}
        <div
          style={{
            padding: 20,
            borderRadius: 12,
            background: "rgba(15,23,42,0.8)",
            border: "1px solid rgba(124,255,178,0.2)",
            backdropFilter: "blur(10px)",
          }}
        >
          <h3 style={{ fontSize: 16, fontWeight: 700, marginTop: 0, marginBottom: 16, color: "#f8fafc" }}>
            Info Pubblicazione
          </h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div
              style={{
                padding: 12,
                background: "rgba(16,185,129,0.1)",
                borderRadius: 8,
                border: "1px solid rgba(16,185,129,0.2)",
              }}
            >
              <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 4 }}>
                Percentuale Pubblicazioni IPFS
              </div>
              <div style={{ fontSize: 24, fontWeight: 700, color: "#10b981" }}>
                {ipfsRate}%
              </div>
            </div>

            {ipfsMetrics.lastPublishedAt && (
              <div
                style={{
                  padding: 12,
                  background: "rgba(59,130,246,0.1)",
                  borderRadius: 8,
                  border: "1px solid rgba(59,130,246,0.2)",
                }}
              >
                <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 4 }}>
                  Ultimo Pubblicato
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#3b82f6" }}>
                  {formatEnglishDate(ipfsMetrics.lastPublishedAt)}
                </div>
              </div>
            )}

            <button
              onClick={fetchStats}
              style={{
                marginTop: 8,
                padding: "10px 16px",
                background: "linear-gradient(135deg, #3b82f6, #2563eb)",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                cursor: "pointer",
                fontSize: 13,
                fontWeight: 600,
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = "translateY(-2px)";
                e.target.style.boxShadow = "0 8px 16px rgba(59,130,246,0.3)";
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = "none";
              }}
            >
              Aggiorna Dati
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper functions
function getLevelLabel(level) {
  const labels = {
    NONE: "Not Certified",
    VERIFIED: "Verified",
    TRUSTED: "Trusted",
    IMPACT: "Impact",
    REGEN: "Regenerative",
  };
  return labels[level] || level;
}

function getLevelColor(level) {
  const colors = {
    NONE: "#9CA3AF",
    VERIFIED: "#F97316",
    TRUSTED: "#06B6D4",
    IMPACT: "#FFEB3B",
    REGEN: "#84CC16",
  };
  return colors[level] || "#9CA3AF";
}

function getTopLevel(levelDistribution) {
  let topLevel = "NONE";
  let maxCount = 0;
  for (const [level, count] of Object.entries(levelDistribution)) {
    if (count > maxCount) {
      maxCount = count;
      topLevel = level;
    }
  }
  return topLevel;
}

function getEvidenceTypeColor(type) {
  const colors = {
    DOCUMENT: "#6B7280",
    AUDIT: "#3B82F6",
    CERTIFICATION: "#10B981",
    INSPECTION: "#8B5CF6",
  };
  return colors[type] || "#9CA3AF";
}

export default AdminDashboardStats;
